import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, SpecraBadge, SpecraButton, SpecraSectionHeader, SpecraTextarea, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useProgramContext } from '../../../layouts/ProgramLayout';
import { formatLocalYmd, parseLocalDate } from '../_shared/api/nullable';
import { fromSelectValue, labelForOption, toSelectValue, withNoneOption, withStoredFallback } from '../_shared/api/selectOptions';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { DrawerDocumentsSection } from '../_shared/components/DrawerDocumentsSection';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { RolePicker } from '../_shared/components/RolePicker';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useContractCLINs } from '../Programs/contractHooks';
import { issueHooks, occurrenceLinkFor, riskHooks, useAddIssueComment, useDeliverableLinkOptions, useIssueComments } from './hooks';
import { ISSUE_IMPACT, ISSUE_PRIORITY, ISSUE_STATE } from './options';
// Terminal states for an Issue — Resolve Issue hides once already reached.
const ISSUE_RESOLVED_STATES = new Set([
    'Resolved',
    'Closed'
]);
// The Update History drawer section: a reverse-chronological comment feed
// (server already returns newest-first) plus an add-comment control. Read-only
// when `disabled` (view-only mode).
function UpdateHistorySection({ programId, issueId, disabled }) {
    const { data: comments = [] } = useIssueComments(programId, issueId);
    const addComment = useAddIssueComment(programId, issueId);
    const [body, setBody] = useState('');
    const handleAdd = async ()=>{
        const trimmed = body.trim();
        if (!trimmed) return;
        await addComment.mutateAsync(trimmed);
        setBody('');
    };
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.deliveryRisk.issueHistory,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "Update History"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-2",
                children: [
                    comments.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                        children: "No comments yet."
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: comments.map((c)=>/*#__PURE__*/ _jsxs("li", {
                                className: "text-[length:var(--fs-ui-body)]",
                                "data-testid": Testids.deliveryRisk.issueHistoryRow,
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-baseline justify-between gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium text-foreground",
                                                children: c.authorName || 'Unknown user'
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "shrink-0 text-[length:var(--fs-ui-meta)] tabular-nums text-muted-foreground",
                                                children: c.createdAt ? c.createdAt.toDate().toISOString().slice(0, 10) : '—'
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "mt-0.5 whitespace-pre-wrap break-words text-foreground",
                                        children: c.body
                                    })
                                ]
                            }, c.id))
                    })
                ]
            }),
            !disabled && /*#__PURE__*/ _jsxs("div", {
                className: "mt-3 space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: body,
                        onChange: (e)=>setBody(e.target.value),
                        placeholder: "Add a comment…",
                        rows: 2,
                        "data-testid": Testids.deliveryRisk.issueCommentInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>void handleAdd(),
                        disabled: !body.trim() || addComment.isPending,
                        "data-testid": Testids.deliveryRisk.issueCommentSubmit,
                        children: addComment.isPending ? 'Adding…' : 'Add comment'
                    })
                ]
            })
        ]
    });
}
const ymd = (v)=>v ? formatLocalYmd(v) ?? '—' : '—';
// Provenance label from autoGeneratedType: the risk-conversion path gets a
// specific label, any other non-empty value (e.g. get-well-plan issues) gets
// the generic one. Empty renders nothing.
const provenanceLabel = (autoGeneratedType)=>{
    if (!autoGeneratedType) return null;
    if (autoGeneratedType === 'risk_conversion') return 'Converted from potential issue';
    return 'Auto-generated';
};
function ProvenanceBadge({ autoGeneratedType }) {
    const label = provenanceLabel(autoGeneratedType);
    if (!label) return null;
    return /*#__PURE__*/ _jsx(SpecraBadge, {
        variant: "neutral",
        "data-testid": Testids.deliveryRisk.issueProvenanceBadge,
        children: label
    });
}
// Front columns are the at-a-glance triage fields; the rest (assigned to, root
// cause, corrective action, target date) lives in the detail drawer opened by
// clicking a row — read by default, edited in place from the row's Edit action.
const columns = [
    {
        key: 'description',
        header: 'Description',
        render: (i)=>/*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        children: i.description
                    }),
                    /*#__PURE__*/ _jsx(ProvenanceBadge, {
                        autoGeneratedType: i.autoGeneratedType
                    })
                ]
            })
    },
    {
        key: 'priority',
        header: 'Priority'
    },
    {
        key: 'impact',
        header: 'Impact'
    },
    {
        key: 'state',
        header: 'State'
    }
];
// Impact was free text before it became a select; an editing row whose value
// is not in the canonical list gets it prepended as a "(legacy)" option so
// the trigger shows the real string instead of blanking, and saving without
// touching it round-trips the exact value — severity ranking of old rows is
// unchanged.
const impactOptionsFor = (current)=>{
    if (current && !ISSUE_IMPACT.some((o)=>o.value === current)) {
        return [
            {
                value: current,
                label: `${current} (legacy)`
            },
            ...ISSUE_IMPACT
        ];
    }
    return ISSUE_IMPACT;
};
const fieldsFor = (impactOptions, clins, deliverables)=>[
        {
            kind: 'textarea',
            name: 'description',
            label: 'Description',
            required: true,
            rows: 2
        },
        {
            kind: 'select',
            name: 'priority',
            label: 'Priority',
            required: true,
            options: ISSUE_PRIORITY
        },
        {
            kind: 'select',
            name: 'impact',
            label: 'Impact',
            options: impactOptions
        },
        {
            kind: 'select',
            name: 'state',
            label: 'State',
            required: true,
            options: ISSUE_STATE
        },
        // Assigned to is the RolePicker in extraContent (see RisksTab owner).
        {
            kind: 'select',
            name: 'linkedClinId',
            label: 'Linked CLIN',
            options: clins
        },
        {
            kind: 'select',
            name: 'linkedDutyId',
            label: 'Linked deliverable',
            options: deliverables,
            testId: Testids.cmRiskLinks.issueDeliverable
        },
        {
            kind: 'textarea',
            name: 'rootCause',
            label: 'Root cause',
            rows: 2
        },
        {
            kind: 'date',
            name: 'targetResolutionDate',
            label: 'Target resolution date'
        }
    ];
export function IssuesTab({ programId }) {
    const { canEdit } = useProgramContext();
    const { data, isLoading } = issueHooks.useList({
        programId
    });
    const upsert = issueHooks.useUpsert();
    const remove = issueHooks.useDelete();
    const items = data?.items ?? [];
    // Already cached by the sibling Risks tab — reused here to resolve a
    // converted Issue's source potential issue for the Provenance section.
    const risks = riskHooks.useList({
        programId
    }).data?.items ?? [];
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [detailId, setDetailId] = useState(null);
    // Derive the viewed record from `items` (store only the id) so an open
    // drawer always reflects the latest list without a re-sync effect that
    // infinite-loops when the list returns fresh-but-equal refs each render.
    const detail = detailId ? items.find((r)=>r.id === detailId) ?? null : null;
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    // Assigned to is parent-controlled (like the risk owner): the RolePicker fills
    // the contact id + display name, kept out of the RHF form so a pick never
    // resets the other fields.
    const [assigneeContactId, setAssigneeContactId] = useState('');
    const [assigneeName, setAssigneeName] = useState('');
    // Link sources: the program's CLINs and deliverables (both optional).
    const clins = useContractCLINs(programId).data?.clins ?? [];
    const clinOptions = withNoneOption(clins.map((c)=>({
            value: c.id,
            label: `${c.clinNumber} ${c.shortTitle}`.trim()
        })));
    const deliverableOptions = useDeliverableLinkOptions(programId);
    // Shared by the create modal and drawer edit-in-place; the target id is passed
    // in (empty = create). High Risk is no longer surfaced, but the upsert is
    // full-replace, so carry the stored flag from the source record rather than
    // clearing it on an unrelated edit.
    const submitIssue = async (values, id)=>{
        const src = items.find((r)=>r.id === id);
        const linkedDutyId = fromSelectValue(values.linkedDutyId ?? '');
        await upsert.mutateAsync({
            id,
            programId,
            priority: values.priority,
            impact: values.impact ?? '',
            description: values.description,
            state: values.state,
            // Assigned to is the RolePicker's selection, held in parent state; the
            // name and contact id are set together by onChange / onClear.
            assignedTo: assigneeName,
            assigneeContactId,
            linkedClinId: fromSelectValue(values.linkedClinId ?? ''),
            // The old model's link goes back exactly as stored (a plain string on
            // the wire, so dropping it would clear it).
            linkedDeliverableId: src?.linkedDeliverableId ?? '',
            linkedDutyId,
            linkedOccurrenceId: occurrenceLinkFor(src?.linkedDutyId, linkedDutyId),
            rootCause: values.rootCause ?? '',
            highRiskFlag: src?.highRiskFlag ?? false,
            targetResolutionDate: values.targetResolutionDate ? Timestamp.fromDate(parseLocalDate(values.targetResolutionDate)) : undefined
        });
    };
    // Seed the out-of-form assignee picker from a record.
    const seedAssigneeFrom = (r)=>{
        setAssigneeContactId(r?.assigneeContactId ?? '');
        setAssigneeName(r?.assignedTo ?? '');
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    // Resolve Issue: a full-replace upsert that flips state only, carrying every
    // other field from the source record unchanged (same discipline as the
    // stored highRiskFlag above) — assignedTo/assigneeContactId included, since
    // the drawer may be in read mode with no seeded picker state. The server
    // auto-stamps resolved_at on the first transition into Resolved/Closed, so
    // no timestamp is sent here.
    const [resolveConfirmOpen, setResolveConfirmOpen] = useState(false);
    const handleResolveConfirm = async ()=>{
        if (!detail) return;
        await upsert.mutateAsync({
            id: detail.id,
            programId,
            priority: detail.priority,
            impact: detail.impact,
            description: detail.description,
            state: 'Resolved',
            assignedTo: detail.assignedTo,
            assigneeContactId: detail.assigneeContactId,
            linkedClinId: detail.linkedClinId,
            linkedDeliverableId: detail.linkedDeliverableId,
            rootCause: detail.rootCause,
            highRiskFlag: detail.highRiskFlag,
            targetResolutionDate: detail.targetResolutionDate
        });
        setResolveConfirmOpen(false);
    };
    const openCreate = ()=>{
        setEditing(null);
        seedAssigneeFrom(null);
        setFormOpen(true);
    };
    // The assignee picker — shared verbatim by the create modal (extraContent) and
    // the drawer's inline edit (the Resolution "Assigned to" row via editControl).
    // Both drive the same parent state.
    const assigneePicker = /*#__PURE__*/ _jsx(RolePicker, {
        id: "assigneeContactId",
        label: "Assigned to",
        helpText: "Pick from this program's internal roles. Someone not listed must be added under Roles first.",
        programId: programId,
        value: assigneeContactId,
        fallbackLabel: assigneeName,
        onClear: ()=>{
            setAssigneeContactId('');
            setAssigneeName('');
        },
        onChange: (cid, name)=>{
            setAssigneeContactId(cid);
            setAssigneeName(name);
        }
    });
    // Build the issue form config (fields + defaults) for a given record — reused
    // by the create modal (editing) and the drawer edit (detail).
    const issueFormConfig = (r)=>({
            fields: fieldsFor(impactOptionsFor(r?.impact ?? ''), withStoredFallback(clinOptions, r?.linkedClinId ?? ''), withStoredFallback(deliverableOptions, r?.linkedDutyId ?? '')),
            defaultValues: {
                id: r?.id ?? '',
                priority: r?.priority ?? '',
                impact: r?.impact ?? '',
                description: r?.description ?? '',
                state: r?.state ?? 'Open',
                linkedClinId: toSelectValue(r?.linkedClinId ?? ''),
                linkedDutyId: toSelectValue(r?.linkedDutyId ?? ''),
                rootCause: r?.rootCause ?? '',
                targetResolutionDate: formatLocalYmd(r?.targetResolutionDate) ?? ''
            }
        });
    // The drawer's inline-edit config, bound to the record being viewed.
    const detailEdit = detail ? {
        defaultValues: issueFormConfig(detail).defaultValues,
        onSubmit: (v)=>submitIssue(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes',
        onEditStart: ()=>seedAssigneeFrom(detail)
    } : undefined;
    // Detail sections: read values plus, per row, the inline-edit control. Standard
    // fields carry an `editField` (bound to the drawer's form); assigned-to carries
    // an `editControl` (the parent-controlled picker). The link selects reuse the
    // same stored-fallback option lists the modal uses, so a superseded CLIN still
    // shows as "unavailable" instead of blanking.
    const detailClinOptions = withStoredFallback(clinOptions, detail?.linkedClinId ?? '');
    const detailDeliverableOptions = withStoredFallback(deliverableOptions, detail?.linkedDutyId ?? '');
    const detailSections = detail ? [
        {
            heading: 'Issue',
            rows: [
                {
                    label: 'Description',
                    value: detail.description || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'description',
                        label: 'Description',
                        required: true,
                        rows: 2
                    }
                },
                {
                    label: 'Priority',
                    value: detail.priority || '—',
                    editField: {
                        kind: 'select',
                        name: 'priority',
                        label: 'Priority',
                        required: true,
                        options: ISSUE_PRIORITY
                    }
                },
                {
                    label: 'Impact',
                    value: detail.impact || '—',
                    editField: {
                        kind: 'select',
                        name: 'impact',
                        label: 'Impact',
                        options: impactOptionsFor(detail.impact)
                    }
                },
                {
                    label: 'State',
                    value: detail.state || '—',
                    editField: {
                        kind: 'select',
                        name: 'state',
                        label: 'State',
                        required: true,
                        options: ISSUE_STATE
                    }
                }
            ]
        },
        {
            heading: 'Links',
            rows: [
                {
                    label: 'Linked CLIN',
                    value: labelForOption(detail.linkedClinId, clinOptions),
                    editField: {
                        kind: 'select',
                        name: 'linkedClinId',
                        label: 'Linked CLIN',
                        options: detailClinOptions
                    }
                },
                {
                    label: 'Linked deliverable',
                    value: labelForOption(detail.linkedDutyId, deliverableOptions),
                    editField: {
                        kind: 'select',
                        name: 'linkedDutyId',
                        label: 'Linked deliverable',
                        options: detailDeliverableOptions,
                        testId: Testids.cmRiskLinks.issueDeliverable
                    }
                }
            ]
        },
        {
            heading: 'Resolution',
            rows: [
                {
                    label: 'Assigned to',
                    value: detail.assignedTo || '—',
                    editControl: assigneePicker
                },
                {
                    label: 'Root cause',
                    value: detail.rootCause || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'rootCause',
                        label: 'Root cause',
                        rows: 2
                    }
                },
                {
                    label: 'Target resolution',
                    value: ymd(detail.targetResolutionDate),
                    editField: {
                        kind: 'date',
                        name: 'targetResolutionDate',
                        label: 'Target resolution date'
                    }
                }
            ]
        },
        ...detail.linkedRecordId && detail.autoGeneratedType === 'risk_conversion' ? [
            {
                heading: 'Provenance',
                rows: [
                    {
                        label: 'Converted from',
                        // Read-only — the server stamps linkedRecordId on conversion;
                        // there's nothing here for the user to edit.
                        value: risks.find((r)=>r.id === detail.linkedRecordId)?.shortDescription || '—'
                    }
                ]
            }
        ] : []
    ] : [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: openCreate,
                    disabled: !canEdit,
                    "data-testid": Testids.deliveryRisk.issueCreate,
                    children: "New Issue"
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryRisk.issuesTable,
                rowTestid: Testids.deliveryRisk.issueRow,
                rowKey: (i)=>i.id,
                isLoading: isLoading,
                hideRowActions: !canEdit,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                // Edit-in-place: open the drawer straight into edit mode; the
                                // drawer's onEditStart seeds the assignee picker from this record.
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryRisk.issueEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryRisk.issueDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "New Issue",
                ...issueFormConfig(editing),
                extraContent: assigneePicker,
                onSubmit: async (v)=>{
                    await submitIssue(v, editing?.id ?? '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Issue",
                description: deleting ? 'Delete this issue? This cannot be undone.' : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            }),
            /*#__PURE__*/ _jsx(AlertDialog, {
                open: resolveConfirmOpen,
                onOpenChange: setResolveConfirmOpen,
                children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogTitle, {
                                    children: "Resolve Issue"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogDescription, {
                                    children: "Mark this issue as Resolved? You can still edit it afterward."
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogCancel, {
                                    className: specraButtonVariants({
                                        variant: 'secondary'
                                    }),
                                    disabled: upsert.isPending,
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogAction, {
                                    className: specraButtonVariants({
                                        variant: 'default'
                                    }),
                                    disabled: upsert.isPending,
                                    onClick: ()=>void handleResolveConfirm(),
                                    "data-testid": Testids.deliveryRisk.issueResolveConfirm,
                                    children: upsert.isPending ? 'Resolving…' : 'Resolve'
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: "Issue",
                sections: detailSections,
                testid: Testids.deliveryRisk.issueDetailDrawer,
                edit: detailEdit,
                canEdit: canEdit,
                initialMode: detailMode,
                extraHeaderAction: canEdit && detail && !ISSUE_RESOLVED_STATES.has(detail.state) ? /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    variant: "ghost",
                    onClick: ()=>setResolveConfirmOpen(true),
                    "data-testid": Testids.deliveryRisk.issueResolve,
                    className: "text-muted-foreground hover:text-foreground",
                    children: "Resolve Issue"
                }) : undefined,
                children: detail && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(UpdateHistorySection, {
                            programId: programId,
                            issueId: detail.id,
                            disabled: !canEdit
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mt-[var(--space-5-5)] border-t border-border/60 pt-[var(--space-card)]",
                            children: /*#__PURE__*/ _jsx(DrawerDocumentsSection, {
                                programId: programId,
                                scope: {
                                    issueId: detail.id
                                },
                                emptyText: "No documents attached to this issue.",
                                canEdit: canEdit
                            })
                        })
                    ]
                })
            })
        ]
    });
}
